import { useMemo, useCallback, useState, useEffect } from "react";
import { X, Edit2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DynamicModuleService,
  SchemaService,
  DropdownService,
  PtsAttendanceService,
} from "@/services/APIs";
import CustomButton from "@/components/common/Button";
import { Pagination, ConfigProvider } from "antd";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { getEmployeeId } from "@/lib/storeManager";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import { createForm } from "@formily/core";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { notify } from "@/components/common/Notification";

const EMPLOYEE_CHECKIN_SCHEMA_NAME = "PTS Employee Checkin";
const EMPLOYEE_SCHEMA_NAME = "PTS Emp";

interface CheckinRecord {
  id: string;
  employee: string;
  employeeName: string;
  firstName: string;
  lastName: string;
  date: string;
  time: string;
  logType: string;
}

interface EmployeeCheckinTabProps {
  selectedEmployeeId?: string | null;
}

const EmployeeCheckinTab = ({
  selectedEmployeeId,
}: EmployeeCheckinTabProps) => {
  const queryClient = useQueryClient();
  // Use selectedEmployeeId prop if provided, otherwise fallback to getEmployeeId()
  const employeeId = selectedEmployeeId ?? getEmployeeId();

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(300);

  // Edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<{ id: string } | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useMemo(() => createForm({}), []);

  // Calculate default dates (6 days before today to today)
  const getDefaultDates = useCallback(() => {
    const today = new Date();
    const endDateStr = today.toISOString().split("T")[0]; // YYYY-MM-DD format
    const sixDaysInMs = 6 * 24 * 60 * 60 * 1000;
    const startDate = new Date(today.getTime() - sixDaysInMs);
    const startDateStr = startDate.toISOString().split("T")[0]; // YYYY-MM-DD format
    return {
      startDate: startDateStr,
      endDate: endDateStr,
    };
  }, []);

  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState<string>(defaultDates.startDate);
  const [endDate, setEndDate] = useState<string>(defaultDates.endDate);

  // Format date for API (YYYY-MM-DDTHH:mm:ss)
  // Start date: 00:00:00, End date: 23:59:59
  const formatDateForAPI = (dateString: string, isEndDate: boolean = false) => {
    if (!dateString) {
      return null;
    }
    const [year, month, day] = dateString.split("-").map(Number);
    if (isEndDate) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T23:59:59`;
    } else {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T00:00:00`;
    }
  };

  // Fetch column grid data to find field names
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", EMPLOYEE_CHECKIN_SCHEMA_NAME],
    queryFn: () =>
      SchemaService.getColumnGridData(EMPLOYEE_CHECKIN_SCHEMA_NAME),
  });

  // Fetch schema for edit modal form
  const { data: schemaInfo } = useQuery({
    queryKey: ["schema", EMPLOYEE_CHECKIN_SCHEMA_NAME],
    queryFn: () => SchemaService.getSchemaByName(EMPLOYEE_CHECKIN_SCHEMA_NAME),
    enabled: !!EMPLOYEE_CHECKIN_SCHEMA_NAME && modalOpen,
  });

  // Fetch record data for edit mode
  const { data: recordInfo } = useQuery({
    queryKey: [
      "dynamic-model",
      EMPLOYEE_CHECKIN_SCHEMA_NAME,
      editingRecord?.id,
    ],
    queryFn: () => {
      if (!editingRecord?.id) {
        throw new Error("Record ID is required");
      }
      return DynamicModuleService.getItemById(
        EMPLOYEE_CHECKIN_SCHEMA_NAME,
        editingRecord.id
      );
    },
    enabled: modalOpen && !!editingRecord?.id,
  });

  // Fetch employee dropdown for employee name mapping
  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Fetch employee data to get first_name and last_name
  const { data: employeeDataResponse } = useQuery({
    queryKey: ["employee-data", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => {
      return DynamicModuleService.getGridColItems(
        EMPLOYEE_SCHEMA_NAME,
        1,
        1000,
        {}
      );
    },
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Extract employee records
  const employeeRecords = useMemo(() => {
    if (!employeeDataResponse) {
      return [];
    }
    if (Array.isArray(employeeDataResponse)) {
      return employeeDataResponse;
    }
    if (
      employeeDataResponse.results &&
      Array.isArray(employeeDataResponse.results)
    ) {
      return employeeDataResponse.results;
    }
    return [];
  }, [employeeDataResponse]);

  // Get employee column grid data to find first_name and last_name fields
  const { data: employeeColumnGridData } = useQuery({
    queryKey: ["columnGridData", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(EMPLOYEE_SCHEMA_NAME),
  });

  // Find employee field names
  const findEmployeeFieldName = useCallback(
    (searchTerms: string[]) => {
      if (!employeeColumnGridData?.grid) {
        return null;
      }
      for (const field of employeeColumnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(term => {
            const lowerTerm = term.toLowerCase();
            return (
              fieldName.includes(lowerTerm) ?? fieldTitle.includes(lowerTerm)
            );
          })
        ) {
          return field.field_name;
        }
      }
      return null;
    },
    [employeeColumnGridData]
  );

  const employeeFirstNameField = findEmployeeFieldName([
    "first_name",
    "firstname",
    "fname",
  ]);
  const employeeLastNameField = findEmployeeFieldName([
    "last_name",
    "lastname",
    "lname",
  ]);

  // Fetch checkin data with pagination
  const { data: checkinDataResponse, isLoading } = useQuery({
    queryKey: [
      "employee-checkin-data",
      EMPLOYEE_CHECKIN_SCHEMA_NAME,
      startDate,
      endDate,
      employeeId,
      currentPage,
      pageSize,
    ],
    queryFn: () => {
      const paramsFilter: any = {};

      // Add date filters
      if (startDate) {
        const formattedStartDate = formatDateForAPI(startDate, false);
        if (formattedStartDate) {
          paramsFilter.datetime__gte = formattedStartDate;
        }
      }
      if (endDate) {
        const formattedEndDate = formatDateForAPI(endDate, true);
        if (formattedEndDate) {
          paramsFilter.datetime__lte = formattedEndDate;
        }
      }

      // Add employee ID filter
      if (employeeId) {
        paramsFilter.employee__eq = employeeId;
      }

      paramsFilter.ordering = "-datetime";

      return DynamicModuleService.getItemsByPage(
        EMPLOYEE_CHECKIN_SCHEMA_NAME,
        currentPage,
        pageSize,
        paramsFilter
      );
    },
    enabled: !!EMPLOYEE_CHECKIN_SCHEMA_NAME,
  });

  // Extract data from response
  const checkinRecords = useMemo(() => {
    if (!checkinDataResponse) {
      return [];
    }
    if (Array.isArray(checkinDataResponse)) {
      return checkinDataResponse;
    }
    if (
      checkinDataResponse.results &&
      Array.isArray(checkinDataResponse.results)
    ) {
      return checkinDataResponse.results;
    }
    return [];
  }, [checkinDataResponse]);

  // Get total count from response
  const totalCount = useMemo(() => {
    if (!checkinDataResponse) {
      return 0;
    }
    if (typeof checkinDataResponse.count === "number") {
      return checkinDataResponse.count;
    }
    // Fallback: if no count, use array length
    if (Array.isArray(checkinDataResponse)) {
      return checkinDataResponse.length;
    }
    if (
      checkinDataResponse.results &&
      Array.isArray(checkinDataResponse.results)
    ) {
      return checkinDataResponse.results.length;
    }
    return 0;
  }, [checkinDataResponse]);

  // Find field names from schema
  const findFieldName = useCallback(
    (searchTerms: string[]) => {
      if (!columnGridData?.grid) {
        return null;
      }
      for (const field of columnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(term => {
            const lowerTerm = term.toLowerCase();
            return (
              fieldName.includes(lowerTerm) ?? fieldTitle.includes(lowerTerm)
            );
          })
        ) {
          return field.field_name;
        }
      }
      return null;
    },
    [columnGridData]
  );

  // Get field names
  const employeeField = findFieldName([
    "employee",
    "employee_id",
    "emp_id",
    "empname",
  ]);
  const dateField = findFieldName([
    "date",
    "checkin_date",
    "check_in_date",
    "attendance_date",
    "work_date",
  ]);
  const timeField = findFieldName([
    "time",
    "checkin_time",
    "check_in_time",
    "timestamp",
    "datetime",
  ]);
  const logTypeField = findFieldName([
    "logtype",
    "log_type",
    "type",
    "check_type",
  ]);

  // Get employee name by ID
  const getEmployeeNameById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id || !employeeOptions) {
        return "-";
      }
      const employee = employeeOptions.find(
        (item: { value: string; label: string }) =>
          String(item.value) === String(id)
      );
      return employee?.label ?? "-";
    },
    [employeeOptions]
  );

  // Get employee first and last name by ID
  const getEmployeeNamesById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return { firstName: "-", lastName: "" };
      }

      const employee = employeeRecords.find(
        (record: any) =>
          String(
            record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
          ) === String(id)
      );

      if (!employee) {
        // Fallback to dropdown label if employee record not found
        const dropdownEmployee = employeeOptions?.find(
          (item: { value: string; label: string }) =>
            String(item.value) === String(id)
        );
        if (dropdownEmployee?.label) {
          const nameParts = dropdownEmployee.label.split(" ");
          return {
            firstName: nameParts[0] ?? "-",
            lastName: nameParts.slice(1).join(" ") ?? "",
          };
        }
        return { firstName: "-", lastName: "" };
      }

      const firstName =
        (employeeFirstNameField && employee[employeeFirstNameField]) ??
        employee.first_name ??
        employee.firstname ??
        employee.fname ??
        "";
      const lastName =
        (employeeLastNameField && employee[employeeLastNameField]) ??
        employee.last_name ??
        employee.lastname ??
        employee.lname ??
        "";

      return {
        firstName: firstName ?? "-",
        lastName: lastName ?? "",
      };
    },
    [
      employeeRecords,
      employeeOptions,
      employeeFirstNameField,
      employeeLastNameField,
    ]
  );

  // Format date helper - DD-MM-YYYY
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) {
      return "-";
    }
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
      return dateString;
    } catch {
      return dateString;
    }
  };

  // Format time helper
  const formatTime = (timeString: string | null | undefined): string => {
    if (!timeString) {
      return "-";
    }
    try {
      // Handle ISO date strings
      const date = new Date(timeString);
      if (!isNaN(date.getTime())) {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const period = hours >= 12 ? "PM" : "AM";
        const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
        return `${String(displayHour).padStart(2, "0")}:${String(
          minutes
        ).padStart(2, "0")} ${period}`;
      }
      // Handle time strings like "HH:MM:SS"
      const [hours, minutes] = timeString.split(":");
      if (hours && minutes) {
        const hour = parseInt(hours, 10);
        const period = hour >= 12 ? "PM" : "AM";
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        return `${String(displayHour).padStart(2, "0")}:${minutes} ${period}`;
      }
      return timeString;
    } catch {
      return timeString;
    }
  };

  // Map raw data to CheckinRecord interface
  const mappedRecords = useMemo(() => {
    return checkinRecords.map((record: any): CheckinRecord => {
      const recordId =
        record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? "";

      const employeeId =
        (employeeField && record[employeeField]) ??
        record.employee ??
        record.employee_id ??
        null;

      const employeeNames = getEmployeeNamesById(employeeId);

      // Get date and time - if timeField contains datetime, extract both
      const dateTimeValue =
        (timeField && record[timeField]) ??
        record.time ??
        record.checkin_time ??
        record.timestamp ??
        record.datetime ??
        "";

      // Try to extract date from datetime field if it's a full datetime string
      let dateValue = "";
      let timeValue = "";

      if (dateTimeValue) {
        try {
          const date = new Date(dateTimeValue);
          if (!isNaN(date.getTime())) {
            // It's a valid datetime, extract both date and time
            dateValue = dateTimeValue;
            timeValue = dateTimeValue;
          } else {
            // Not a datetime, might be separate fields
            timeValue = dateTimeValue;
          }
        } catch {
          timeValue = dateTimeValue;
        }
      }

      // If dateField exists, use it; otherwise use the datetime value
      const finalDateValue =
        (dateField && record[dateField]) ??
        record.date ??
        record.checkin_date ??
        record.attendance_date ??
        dateValue ??
        "";

      return {
        id: String(recordId),
        employee: String(employeeId ?? ""),
        employeeName: getEmployeeNameById(employeeId),
        firstName: employeeNames.firstName,
        lastName: employeeNames.lastName,
        date: finalDateValue,
        time: timeValue,
        logType:
          (logTypeField && record[logTypeField]) ??
          record.logType ??
          record.log_type ??
          record.type ??
          "-",
      };
    });
  }, [
    checkinRecords,
    employeeField,
    dateField,
    timeField,
    logTypeField,
    getEmployeeNameById,
    getEmployeeNamesById,
  ]);

  // Handle delete
  // const handleDelete = async (id: string | undefined) => {
  //   if (!id) {
  //     notify.error("Record ID is missing");
  //     return;
  //   }
  //   try {
  //     const response = await DynamicModuleService.deleteItem(
  //       EMPLOYEE_CHECKIN_SCHEMA_NAME,
  //       String(id)
  //     );
  //     if (response) {
  //       // notify.success("Check-in record deleted successfully");
  //       queryClient.invalidateQueries({
  //         queryKey: ["employee-checkin-data", EMPLOYEE_CHECKIN_SCHEMA_NAME],
  //       });
  //     }
  //   } catch (error: any) {
  //     notify.error(error?.message ?? "Failed to delete record");
  //   }
  // };

  // Modify schema to disable Employee and Time fields when editing (static list)
  const getModifiedSchema = useCallback(() => {
    if (!schemaInfo) {
      return null;
    }
    const schema = schemaInfo.json ?? schemaInfo;
    const schemaCopy = JSON.parse(JSON.stringify(schema));
    const isEditMode = !!editingRecord?.id;
    if (!isEditMode) {
      return schemaCopy;
    }
    const editDisabledEmployeeKeys = [
      "employee",
      "employee_id",
      "emp_id",
      "empname",
      "emp",
    ];
    const editDisabledTimeKeys = [
      "datetime",
      "time",
      "timestamp",
      "checkin_time",
      "check_in_time",
    ];
    const processProperties = (props: any) => {
      if (!props || typeof props !== "object") {
        return;
      }
      Object.keys(props).forEach(key => {
        const field = props[key];
        if (field && typeof field === "object") {
          const keyLower = key.toLowerCase();
          const componentFieldName = field["x-component-props"]?.field_name
            ? String(field["x-component-props"].field_name).toLowerCase()
            : "";
          const isEmployeeField = editDisabledEmployeeKeys.some(
            k => keyLower === k || componentFieldName === k
          );
          const isTimeField = editDisabledTimeKeys.some(
            k => keyLower === k || componentFieldName === k
          );
          const shouldDisable = isEmployeeField || isTimeField;
          if (shouldDisable) {
            field["x-component-props"] ??= {};
            field["x-component-props"].disabled = true;
            field["x-decorator-props"] ??= {};
            field["x-decorator-props"].readOnly = true;
            field["x-pattern"] = "readOnly";
            field.readOnly = true;
            field["x-readonly"] = true;
            field["x-disabled"] = true;
          }
          if (field.properties) {
            processProperties(field.properties);
          }
          if (field.items?.properties) {
            processProperties(field.items.properties);
          }
        }
      });
    };
    let properties: any = null;
    if (schemaCopy.schema?.properties) {
      properties = schemaCopy.schema.properties;
    } else if (schemaCopy.properties) {
      properties = schemaCopy.properties;
    }
    if (properties) {
      processProperties(properties);
    }
    return schemaCopy;
  }, [schemaInfo, editingRecord]);

  // Handle edit - open modal with record data (same as EmployeeCheckin page)
  const handleEdit = (id: string | undefined) => {
    if (!id) {
      notify.error("Record ID is missing");
      return;
    }
    setEditingRecord({ id });
    setModalOpen(true);
  };

  // Set form initial values when record data is loaded for edit
  useEffect(() => {
    if (!modalOpen) {
      form.setInitialValues({});
      form.reset("*");
      form.setValues({});
      return;
    }
    if (editingRecord?.id && recordInfo) {
      const cleanedData = removeMetaFields(recordInfo);
      form.setInitialValues(cleanedData);
      form.setValues(cleanedData);
    }
  }, [modalOpen, editingRecord, recordInfo, form]);

  // Get filter_date (YYYY-MM-DD) from record datetime and emp_id from record employee for generate_attendance API
  const getGenerateAttendanceParams = useCallback(
    (record: any): { filter_date: string; emp_id: string } | null => {
      if (!record) {
        return null;
      }
      const dateTimeValue =
        (timeField && record[timeField]) ??
        record.datetime ??
        record.time ??
        record.timestamp ??
        "";
      if (!dateTimeValue) {
        return null;
      }
      let filterDate = "";
      try {
        const d = new Date(dateTimeValue);
        if (!isNaN(d.getTime())) {
          filterDate = d.toISOString().split("T")[0]; // YYYY-MM-DD
        }
      } catch {
        if (typeof dateTimeValue === "string" && dateTimeValue.includes("T")) {
          filterDate = dateTimeValue.split("T")[0];
        }
      }
      if (!filterDate) {
        return null;
      }
      const empRaw =
        (employeeField && record[employeeField]) ?? record.employee ?? "";
      const emp_id =
        typeof empRaw === "object" && empRaw && "id" in empRaw
          ? String((empRaw as { id: string }).id)
          : String(empRaw ?? "");
      if (!emp_id) {
        return null;
      }
      return { filter_date: filterDate, emp_id };
    },
    [timeField, employeeField]
  );

  // Handle modal save (update only - same as EmployeeCheckin edit flow)
  const handleModalSave = async () => {
    if (!editingRecord?.id) {
      return;
    }
    try {
      await form.validate();
      const modifiedValues = removeMetaFields(form.values);
      if (!checkFormChanges(modifiedValues, recordInfo, notify)) {
        return;
      }
      setIsSubmitting(true);
      const response = await DynamicModuleService.updateItem(
        EMPLOYEE_CHECKIN_SCHEMA_NAME,
        editingRecord.id,
        modifiedValues
      );
      if (response) {
        queryClient.invalidateQueries({
          queryKey: ["employee-checkin-data", EMPLOYEE_CHECKIN_SCHEMA_NAME],
        });
        queryClient.invalidateQueries({
          queryKey: ["all-log-types", EMPLOYEE_CHECKIN_SCHEMA_NAME],
        });
        // Call generate_attendance API with record's date and employee id (use recordInfo for date/employee of edited record)
        const params = getGenerateAttendanceParams(recordInfo);
        if (params) {
          try {
            await PtsAttendanceService.generateAttendance(
              params.filter_date,
              params.emp_id
            );
          } catch (attendanceError: any) {
            // Log but don't block success - check-in was updated
            console.warn(
              "Generate attendance call failed:",
              attendanceError?.message
            );
          }
        }
        setModalOpen(false);
        setEditingRecord(null);
        form.reset("*");
        form.setValues({});
      }
    } catch (error: any) {
      notify.error(error?.message ?? "Failed to update check-in record");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setModalOpen(false);
    setEditingRecord(null);
    form.reset("*");
    form.setValues({});
  };

  // Get LogType badge color
  const getLogTypeBadgeColor = (logType: string): string => {
    const logTypeLower = logType?.toLowerCase() ?? "";
    if (logTypeLower === "in" || logTypeLower.includes("in")) {
      return "bg-green-100 text-green-700 hover:bg-green-200";
    }
    if (logTypeLower === "out" || logTypeLower.includes("out")) {
      return "bg-red-100 text-red-700 hover:bg-red-200";
    }
    return "bg-blue-100 text-blue-700 hover:bg-blue-200";
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-white">
      <div className="flex items-center justify-between border-b p-3 md:p-4">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-base font-semibold text-gray-900">
            Employee Check-In History
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative inline-flex items-center">
              <label
                htmlFor="start-date"
                className="mr-1 text-xs text-gray-600"
              >
                Start:
              </label>
              <input
                type="date"
                id="start-date"
                value={startDate}
                onChange={e => {
                  setStartDate(e.target.value);
                  setCurrentPage(1); // Reset to first page when date changes
                }}
                className="border-border-input bg-background-input focus:border-primary focus:ring-primary rounded-lg border px-3 py-1 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-1"
              />
              {startDate && startDate !== defaultDates.startDate && (
                <button
                  onClick={() => {
                    const defaults = getDefaultDates();
                    setStartDate(defaults.startDate);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Reset to default"
                  type="button"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="relative inline-flex items-center">
              <label htmlFor="end-date" className="mr-1 text-xs text-gray-600">
                End:
              </label>
              <input
                type="date"
                id="end-date"
                value={endDate}
                onChange={e => {
                  setEndDate(e.target.value);
                  setCurrentPage(1); // Reset to first page when date changes
                }}
                className="border-border-input bg-background-input focus:border-primary focus:ring-primary rounded-lg border px-3 py-1 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-1"
              />
              {endDate && endDate !== defaultDates.endDate && (
                <button
                  onClick={() => {
                    const today = new Date();
                    setEndDate(today.toISOString().split("T")[0]);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Reset to today"
                  type="button"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
        <span className="text-sm text-gray-500">
          {mappedRecords.length} Records
        </span>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="font-semibold text-gray-700">
                Employee Name
              </TableHead>
              <TableHead className="font-semibold text-gray-700">
                Date
              </TableHead>
              <TableHead className="font-semibold text-gray-700">
                Time
              </TableHead>
              <TableHead className="text-center font-semibold text-gray-700">
                Log Type
              </TableHead>
              <TableHead className="pe-8 text-right font-semibold text-gray-700">
                Action
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mappedRecords.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-8 text-center text-gray-500"
                >
                  No check-in records found
                </TableCell>
              </TableRow>
            ) : (
              mappedRecords.map((record: CheckinRecord) => (
                <TableRow key={record.id} className="hover:bg-gray-50">
                  <TableCell className="ps-5 font-medium text-gray-900">
                    {record.firstName} {record.lastName}
                  </TableCell>
                  <TableCell className="text-gray-700">
                    {formatDate(record.date || record.time)}
                  </TableCell>
                  <TableCell className="text-gray-700">
                    {formatTime(record.time)}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      className={`${getLogTypeBadgeColor(record.logType)} border-0 text-xs`}
                    >
                      {record.logType}
                    </Badge>
                  </TableCell>
                  <TableCell className="pe-5">
                    <div className="flex items-center justify-end gap-2">
                      <CustomButton
                        icon={<Edit2 size={14} />}
                        type="text"
                        onClick={() => handleEdit(record.id)}
                        title="Edit"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {/* Edit modal - same as EmployeeCheckin page */}
      <Dialog
        open={modalOpen}
        onOpenChange={open => {
          setModalOpen(open);
          if (!open) {
            setEditingRecord(null);
            form.reset("*");
            form.setValues({});
          }
        }}
      >
        <DialogContent className="bg-white sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Check-In Record</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {schemaInfo ? (
              <ConfigProvider
                getPopupContainer={triggerNode =>
                  triggerNode?.parentElement ?? document.body
                }
                dropdownMatchSelectWidth={false}
              >
                <SchemaFormView
                  key={editingRecord?.id ? `edit-${editingRecord.id}` : "edit"}
                  form={form}
                  schemaJson={getModifiedSchema()}
                />
              </ConfigProvider>
            ) : (
              <div className="flex h-64 items-center justify-center">
                <LoadingSpinner />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleModalCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleModalSave}
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              {isSubmitting ? "Saving..." : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Pagination */}
      <div className="flex items-center justify-end border-t p-4">
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={totalCount}
          onChange={(page, size) => {
            if (size && size !== pageSize) {
              setCurrentPage(1);
              setPageSize(size);
            } else {
              setCurrentPage(page);
            }
          }}
          onShowSizeChange={(_current, size) => {
            setCurrentPage(1);
            setPageSize(size);
          }}
          showSizeChanger
          showQuickJumper
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} of ${total} items`
          }
          pageSizeOptions={["300", "500"]}
          disabled={isLoading}
        />
      </div>
    </div>
  );
};

export default EmployeeCheckinTab;
